import { normalizedTranslate } from '@rbilabs/components-library';
import styled, { css, keyframes } from 'styled-components';

import { ActionButton } from 'components/action-button';
import { DisclaimerContainer } from 'components/offers/ui-refresh/offer-disclaimer';
import { Picture } from 'components/picture';
import { SanityBlockRenderer } from 'components/sanity-block-renderer';
import { primitive } from 'styles/constants/primitives';
import { fadeIn } from 'utils/style/animations';

import { HIGHLIGHT_COLOR } from '../constants';

import { theme } from './theme';

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  flex-direction: column;
  animation: ${fadeIn} 0.65s forwards;
  overflow-y: auto;
  overflow-x: hidden;
  background: ${Styles.color.white};
  padding: 0;
  width: 100vw;

  ${Styles.desktop} {
    width: 100%;
    animation: ${fadeIn} 0.25s forwards;
  }
`;

export const ImageWrapper = styled.div`
  position: relative;
  height: fit-content;
  width: 100%;
  display: flex;
  justify-content: center;
  padding-block-end: 1rem;
  flex-shrink: 0;
  margin-block-end: 1rem;
`;

export const Padding = styled.div`
  min-height: 1rem;
  width: 1px;
`;

export const StyledIncentiveImage = styled(Picture)<{ isOfferClosed?: boolean }>`
  width: 100%;
  margin-block-start: 1rem;
  z-index: ${Styles.zIndex.below};

  & > img {
    border-radius: 0;
  }

  ${Styles.desktop} {
    height: fit-content;
    max-width: 550px;
    border-radius: 0.5rem;
  }

  ${p =>
    p.isOfferClosed &&
    css`
      opacity: 0.5;
    `}
`;

export const BackgroundImage = styled(Picture)`
  position: absolute;
  inset-inline-start: 0;
  inset-block-start: 0;
  width: 100%;
  height: 100%;
`;

const slideRight = keyframes`
  from {
    transform: ${normalizedTranslate('50px')};
  }
  to {
    transform: ${normalizedTranslate(0)};
  }
`;

export const RewardName = styled.h2`
  font-size: 1.25rem;
  font-family: ${Styles.fontFamily.brand};
  line-height: 1.2;
  margin-block-start: 0.5rem;
  margin-block-end: 0;
  margin-inline: 0;
  text-align: center;
  max-width: 60rem;
  animation: ${slideRight} 0.5s forwards;

  ${Styles.desktop} {
    margin-block-start: 1rem;
    margin-block-end: 0;
    margin-inline: 0;
    animation: unset;
    font-size: 2.25rem;
  }
`;

export const RewardSubtitle = styled.h3`
  margin-block-start: 0.5rem;
  margin-block-end: 0;
  margin-inline: 0;
  font-family: ${Styles.fontFamily.body};
  text-align: center;
  font-size: 1rem;
  line-height: 1.3rem;
  color: ${theme.rewardDescriptionColor};
  width: 85%;

  ${Styles.desktop} {
    font-size: 1.25rem;
    line-height: 1.4rem;
    max-width: 45rem;
  }
`;

export const RewardDescription = styled(SanityBlockRenderer)`
  margin-block-start: 1.5rem;
  margin-block-end: 0;
  margin-inline: 0;
  font-family: ${Styles.fontFamily.body};
  text-align: center;
  font-size: 0.812rem;
  line-height: 1.3rem;
  color: ${theme.rewardDescriptionColor};
  width: 85%;

  ${Styles.desktop} {
    font-size: 1rem;
    line-height: 1.4rem;
    max-width: 45rem;
  }
`;

export const ButtonContainer = styled.div`
  flex-shrink: 0;
  margin: 1rem 0;
  width: 100%;

  ${Styles.desktop} {
    margin-block-start: 2rem;
    margin-block-end: 0;
    margin-inline: 0;
    width: auto;
  }

  > * {
    width: 100%;

    :last-child {
      margin-block-start: 1rem;
    }
  }
`;

export const RewardImageWrapper = styled.div`
  position: relative;
  height: fit-content;
  width: 100%;
  display: flex;
  justify-content: center;
  padding-block-end: 1rem;
  flex-shrink: 0;
  margin-block-end: 1rem;
`;

export const RewardDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1rem;
`;

export const DisclaimerText = styled(SanityBlockRenderer)`
  max-width: 36rem;
  text-align: center;
  font-size: 0.75rem;
  font-family: ${Styles.fontFamily.body};
  font-style: normal;
  line-height: 1.33;
  color: ${theme.disclaimerTextColor};
  margin: 1rem auto;

  ${Styles.desktop} {
    margin-block-start: 2rem;
  }
`;

export const ErrorMessage = styled.p`
  text-align: center;
  color: ${Styles.color.error};
`;

export const RewardLabel = styled.div`
  background-color: ${HIGHLIGHT_COLOR};
  padding-block-start: 0.4rem;
  padding-block-end: 0.5rem;
  padding-inline: 0.6rem;
  border-radius: 0.5rem;
  color: ${Styles.color.white};
  margin-block-start: 0.3rem;
  margin-block-end: 0;
  margin-inline: 0.4rem;
  font-size: 0.9rem;
  line-height: 1;

  ${Styles.desktop} {
    display: none;
  }
`;

export const ButtonsWrapper = styled.div`
  position: sticky;
  inset-block-end: 0;
  background-color: ${p => p.theme.primaryBackground};
  display: flex;
  flex-flow: row wrap;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: center;
  z-index: ${Styles.zIndex.top};
  padding: 0 1rem;
  width: 100%;

  ${Styles.desktop} {
    inset-block-end: auto;
    position: static;
    background-color: transparent;
    width: auto;
    display: block;
    padding-block-start: 0;
    padding-block-end: 2rem;
    padding-inline: 1rem;
    z-index: auto;
  }
`;

export const StyledDisclaimerContainer = styled(DisclaimerContainer)`
  margin-block-start: 1rem;
  padding: 0 1.25rem;
`;

export const StyledActionButton = styled(ActionButton)`
  width: 100%;

  ${Styles.desktop} {
    width: auto;
  }
`;

export const StyledSecondaryActionButton = styled(ActionButton)`
  margin-block-start: 1rem;
  justify-content: center;
  color: ${Styles.color.contrastBackground};
`;

export const WrapperButton = styled.div`
  position: absolute;
  z-index: ${Styles.zIndex.top};
  inset-inline-end: 0;
  margin-block-start: 1.8rem;
  margin-block-end: 0;
  margin-inline-start: 0;
  margin-inline-end: 0.8rem;
  padding: 0 0.3rem;
`;

export const LoadingWrapper = styled.div`
  height: 2.125rem;
`;

export const TierRequirementTag = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${primitive.bk.$bbqBrown};
  color: ${Styles.color.contrastBackground};
  width: fit-content;
  margin: 0.5rem 0;
  padding: 0.5rem;
  border-radius: 0.25rem;
`;

export const TierRequirementTagLocked = styled(TierRequirementTag)`
  background: ${primitive.$blackOpacity10};
  color: ${Styles.color.contrastBackground};
`;

export const TierRequirementText = styled.span`
  color: ${primitive.bk.$mayoEggWhite};
  font-size: 0.9rem;
  line-height: 1rem;
  padding-inline-start: 0.25rem;
  font-weight: 400;
`;

export const TierRequirementTextLocked = styled(TierRequirementText)`
  color: ${primitive.bk.$bbqBrown};
`;
